import { plural, t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type {
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditSource,
  DocumentPageMetadataUserEditStatus,
  DocumentPageMetadataUserEditSurface,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  buildDocumentPageMetadataUserEditPut,
  resolvePageMetadataPipelineConfigId,
  SIDEBAR_PAGE_METADATA_USER_EDIT_WRITE,
} from 'features/knowledgeDocumentViewer/utils/buildDocumentPageMetadataUserEditPut'
import {
  buildPageCascadePutsFromDocumentFamilyEdit,
  type FamilyDocumentMetadataCascadeMember,
} from 'features/knowledgeDocumentViewer/utils/buildFamilyCrossSurfaceCascadePuts'
import {
  buildDocumentPageMetadataUserEditPutsForFamily,
  type FamilyPageMetadataSaveMember,
} from 'features/knowledgeDocumentViewer/utils/buildFamilyPageMetadataUserEditPuts'
import { pageMetadataUserEditCommentsForCreate } from 'features/knowledgeDocumentViewer/utils/pageMetadataUserEditComments'
import { resolveMetadataUserEditSaveContext } from 'features/knowledgeDocumentViewer/utils/resolveMetadataUserEditSaveContext'
import { resolveUserEditsWriteSource } from 'features/knowledgeDocumentViewer/utils/resolveUserEditsWriteSource'
import { seedDocumentPageMetadataUserEditCaches } from 'features/knowledgeDocumentViewer/utils/seedDocumentPageMetadataUserEditCaches'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback } from 'react'
import { useSWRConfig } from 'swr'
import {
  PERMISSION_CANTILEVER_DOC_METADATA_USER_EDITS_LABELER,
  PERMISSION_CANTILEVER_DOC_METADATA_USER_EDITS_REVIEWER,
} from 'types'

export type SaveDocumentPageMetadataUserEditArgs = {
  bqDocumentId: string
  pageNum: number
  systemPage: SystemDocumentPageEntry | undefined
  priorEdit: DocumentPageMetadataUserEditEntry | undefined
  userValues: DocumentPageMetadataUserEditValues
  status?: DocumentPageMetadataUserEditStatus
  source?: DocumentPageMetadataUserEditSource
  surface?: DocumentPageMetadataUserEditSurface
  deferReason?: string | null
  shouldHaveMetadata?: boolean | null
  commentText?: string
  successMessage?: string
  blindQa?: boolean
}

export type SaveDocumentPageMetadataUserEditFamilyArgs = {
  members: readonly FamilyPageMetadataSaveMember[]
  userValues: DocumentPageMetadataUserEditValues
}

export type SaveCascadedPageMetadataFromDocumentFamilyArgs = {
  sharedDocumentUserValues: DocumentMetadataUserEditValues
  members: readonly FamilyDocumentMetadataCascadeMember[]
}

export function useSaveDocumentPageMetadataUserEdit(): {
  savePageMetadataUserEdit: (
    args: SaveDocumentPageMetadataUserEditArgs
  ) => Promise<void>
  savePageMetadataUserEditFamily: (
    args: SaveDocumentPageMetadataUserEditFamilyArgs
  ) => Promise<void>
  saveCascadedPageMetadataFromDocumentFamily: (
    args: SaveCascadedPageMetadataFromDocumentFamilyArgs
  ) => Promise<void>
} {
  const { rolodexPut } = useRolodexEdit()
  const { siteID, orgID } = useKnowledgeDocumentDataContext()
  const { selectedPipelineConfigId } = usePipelineConfig()
  const { user, hasPermissions } = useGeckoPermissions()
  const writeSource = resolveUserEditsWriteSource({
    hasReviewer: hasPermissions([
      PERMISSION_CANTILEVER_DOC_METADATA_USER_EDITS_REVIEWER,
    ]),
    hasLabeler: hasPermissions([
      PERMISSION_CANTILEVER_DOC_METADATA_USER_EDITS_LABELER,
    ]),
  })
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { mutate } = useSWRConfig()
  const { ROLODEX_URL } = useRuntimeVariables()
  const rolodexLoadEndpoint = `${ROLODEX_URL}/api/v2/entries/load`

  const savePageMetadataUserEdit = useCallback(
    async (args: SaveDocumentPageMetadataUserEditArgs) => {
      const { siteID: siteId, orgID: organizationId } =
        resolveMetadataUserEditSaveContext({
          userEmail: user?.email,
          siteID,
          orgID,
          surface: 'page',
          errorNotification,
        })

      const put = buildDocumentPageMetadataUserEditPut({
        siteId,
        organizationId,
        bqDocumentId: args.bqDocumentId,
        pageNum: args.pageNum,
        systemPage: args.systemPage,
        priorEdit: args.priorEdit,
        userValues: args.userValues,
        status: args.status ?? SIDEBAR_PAGE_METADATA_USER_EDIT_WRITE.status,
        source: writeSource,
        surface: args.surface ?? SIDEBAR_PAGE_METADATA_USER_EDIT_WRITE.surface,
        pipelineConfigId: resolvePageMetadataPipelineConfigId(
          args.systemPage,
          selectedPipelineConfigId
        ),
        deferReason: args.deferReason,
        shouldHaveMetadata: args.shouldHaveMetadata,
        comments: pageMetadataUserEditCommentsForCreate(
          args.commentText,
          user?.email ?? '',
          new Date().toISOString()
        ),
        blindQa: args.blindQa,
      })

      try {
        await rolodexPut([put])
        successNotification(args.successMessage ?? t`Page metadata updated`, {
          duration: 2,
          placement: 'topRight',
        })
      } catch (error) {
        console.error('Error saving page metadata user edit:', error)
        errorNotification(
          t`Failed to save page metadata. Confirm cantilever hosts exist for this page.`
        )
        throw error
      }
    },
    [
      errorNotification,
      orgID,
      rolodexPut,
      selectedPipelineConfigId,
      siteID,
      successNotification,
      user?.email,
      writeSource,
    ]
  )

  const savePageMetadataUserEditFamily = useCallback(
    async (args: SaveDocumentPageMetadataUserEditFamilyArgs) => {
      const { siteID: siteId, orgID: organizationId } =
        resolveMetadataUserEditSaveContext({
          userEmail: user?.email,
          siteID,
          orgID,
          surface: 'page',
          errorNotification,
        })
      if (args.members.length === 0) {
        errorNotification(t`No revision family members to save`)
        throw new Error('Empty revision family save')
      }

      const puts = buildDocumentPageMetadataUserEditPutsForFamily({
        siteId,
        organizationId,
        ...SIDEBAR_PAGE_METADATA_USER_EDIT_WRITE,
        source: writeSource,
        pipelineConfigIdFallback: selectedPipelineConfigId,
        sharedUserValues: args.userValues,
        members: args.members,
      })

      const revisionCount = args.members.length
      try {
        await rolodexPut(puts)
        successNotification(
          t`Page metadata updated for ${plural(revisionCount, { one: '# revision', other: '# revisions' })}`,
          {
            duration: 2,
            placement: 'topRight',
          }
        )
      } catch (error) {
        console.error('Error saving family page metadata user edits:', error)
        errorNotification(
          t`Failed to save page metadata for all revisions. Confirm cantilever hosts exist.`
        )
        throw error
      }
    },
    [
      errorNotification,
      orgID,
      rolodexPut,
      selectedPipelineConfigId,
      siteID,
      successNotification,
      user?.email,
      writeSource,
    ]
  )

  const saveCascadedPageMetadataFromDocumentFamily = useCallback(
    async (args: SaveCascadedPageMetadataFromDocumentFamilyArgs) => {
      const { siteID: siteId, orgID: organizationId } =
        resolveMetadataUserEditSaveContext({
          userEmail: user?.email,
          siteID,
          orgID,
          surface: 'page',
          errorNotification,
        })

      const puts = buildPageCascadePutsFromDocumentFamilyEdit({
        siteId,
        organizationId,
        ...SIDEBAR_PAGE_METADATA_USER_EDIT_WRITE,
        source: writeSource,
        pipelineConfigIdFallback: selectedPipelineConfigId,
        sharedDocumentUserValues: args.sharedDocumentUserValues,
        members: args.members,
      })
      if (puts.length === 0) return

      try {
        await rolodexPut(puts)
        await seedDocumentPageMetadataUserEditCaches({
          endpoint: rolodexLoadEndpoint,
          siteId,
          puts,
          mutate,
        })
      } catch (error) {
        console.error(
          'Error cascading page metadata from document family edit:',
          error
        )
        errorNotification(
          t`Failed to update Page Details for all revisions. Confirm cantilever hosts exist.`
        )
        throw error
      }
    },
    [
      errorNotification,
      mutate,
      orgID,
      rolodexLoadEndpoint,
      rolodexPut,
      selectedPipelineConfigId,
      siteID,
      user?.email,
      writeSource,
    ]
  )

  return {
    savePageMetadataUserEdit,
    savePageMetadataUserEditFamily,
    saveCascadedPageMetadataFromDocumentFamily,
  }
}
